
import React from 'react';
import { format, parseISO, isAfter } from 'date-fns';
import { SiteBooking } from '@/hooks/useSiteBookings';

interface BookingTooltipProps {
  bookings: SiteBooking[];
  siteName: string;
  isAvailable?: boolean;
}

const BookingTooltip: React.FC<BookingTooltipProps> = ({ 
  bookings, 
  siteName, 
  isAvailable 
}) => {
  const today = new Date();
  
  // Find current booking (overlaps with today)
  const currentBooking = bookings.find(booking => {
    const startDate = parseISO(booking.start_date);
    const endDate = parseISO(booking.end_date);
    return startDate <= today && endDate > today;
  });

  // Find next booking after today
  const futureBookings = bookings.filter(booking => {
    const startDate = parseISO(booking.start_date);
    return isAfter(startDate, today);
  }).slice(0, 3); // Show max 3 upcoming bookings

  // Find when site becomes available again
  const nextAvailableDate = () => {
    if (currentBooking) {
      const currentEndDate = parseISO(currentBooking.end_date);
      const nextBooking = futureBookings.find(booking => {
        const startDate = parseISO(booking.start_date);
        return startDate.getTime() === currentEndDate.getTime();
      });
      
      if (!nextBooking) {
        return format(currentEndDate, 'MMM d, yyyy');
      }
    }
    return null;
  };

  if (isAvailable && futureBookings.length === 0) {
    return (
      <div className="text-sm">
        <div className="font-medium text-green-700">{siteName}</div>
        <div className="text-green-600">Available for booking</div>
      </div>
    );
  }

  return (
    <div className="text-sm max-w-64">
      <div className="font-medium text-gray-900 mb-1">{siteName}</div>
      
      {currentBooking && (
        <div className="text-orange-700 mb-2">
          <div className="font-medium">Currently Booked</div>
          <div>
            {format(parseISO(currentBooking.start_date), 'MMM d')} - {format(parseISO(currentBooking.end_date), 'MMM d, yyyy')}
          </div>
          {nextAvailableDate() && (
            <div className="text-green-600 text-xs mt-1">
              Available again: {nextAvailableDate()}
            </div>
          )}
        </div>
      )}

      {futureBookings.length > 0 && (
        <div>
          <div className="font-medium text-gray-700 mb-1">
            Upcoming Bookings:
          </div>
          {futureBookings.map((booking, index) => (
            <div key={booking.id} className="text-orange-600 text-xs">
              {format(parseISO(booking.start_date), 'MMM d')} - {format(parseISO(booking.end_date), 'MMM d, yyyy')}
            </div>
          ))}
          {bookings.length > futureBookings.length + (currentBooking ? 1 : 0) && (
            <div className="text-gray-500 text-xs mt-1">
              +{bookings.length - futureBookings.length - (currentBooking ? 1 : 0)} more...
            </div>
          )}
        </div>
      )}

      {!currentBooking && futureBookings.length === 0 && (
        <div className="text-green-600">Available for booking</div>
      )}
    </div>
  );
};

export default BookingTooltip;
